import { useState, useEffect, useRef } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import AdminLayout from "../components/AdminLayout";
import { useContent, CONTENT_DEFAULTS } from "../context/ContentContext";
import { Save, RotateCcw, Image as ImageIcon, X } from "lucide-react";
import { formatApiError } from "../lib/errors";
import { compressImage, uploadImage } from "../lib/image";

function fileToBase64(file) {
    return uploadImage(file, api).catch(() => compressImage(file)).catch(() => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    });
}

const sections = [
    {
        key: "brand",
        title: "Brand & Global Images",
        description: "Logo (used in navbar + footer) and the gold texture used on the 'Open Roles' card. Leave empty to use the default.",
        fields: [
            { k: "logo", label: "Logo (navbar + footer)", type: "image" },
            { k: "texture_gold", label: "Gold texture (Open Roles card background)", type: "image" },
        ],
    },
    {
        key: "hero",
        title: "Hero",
        description: "Top of the page — the first thing visitors see.",
        fields: [
            { k: "eyebrow", label: "Eyebrow tag (small caps above title)", type: "text" },
            { k: "title_line1", label: "Title — line 1", type: "text" },
            { k: "title_line2", label: "Title — line 2 (gold)", type: "text" },
            { k: "title_line3", label: "Title — line 3", type: "text" },
            { k: "paragraph", label: "Subtitle paragraph", type: "textarea" },
            { k: "cta_primary", label: "Primary button label", type: "text" },
            { k: "cta_secondary", label: "Secondary button label", type: "text" },
            { k: "side_rail", label: "Vertical side text", type: "text" },
            { k: "background_image", label: "Background image", type: "image" },
        ],
    },
    {
        key: "about",
        title: "About",
        description: "Section 01 — meaning of Meraki + group intro.",
        fields: [
            { k: "eyebrow", label: "Eyebrow", type: "text" },
            { k: "heading_line1", label: "Heading line 1", type: "text" },
            { k: "heading_emph", label: "Heading emphasis word (gold italic)", type: "text" },
            { k: "heading_line2", label: "Heading line 2", type: "text" },
            { k: "meraki_definition", label: "Meraki definition", type: "textarea" },
            { k: "body", label: "Body paragraph", type: "textarea" },
            { k: "instagram_label", label: "Instagram button label", type: "text" },
            { k: "facebook_label", label: "Facebook button label", type: "text" },
            { k: "image_1", label: "Collage image — top right (large)", type: "image" },
            { k: "image_2", label: "Collage image — bottom left", type: "image" },
            { k: "image_3", label: "Accent image (small, rotated)", type: "image" },
        ],
    },
    {
        key: "rule",
        title: "Group Rule (Marquee)",
        description: "The scrolling rule banner.",
        fields: [
            { k: "phrase", label: "Marquee phrase (include trailing separator)", type: "text" },
            { k: "subtitle", label: "Subtitle below marquee", type: "text" },
        ],
    },
    {
        key: "how",
        title: "How We Work",
        description: "Section 02 — Governor + Ambassador structure.",
        fields: [
            { k: "eyebrow", label: "Eyebrow", type: "text" },
            { k: "heading_line1", label: "Heading line 1", type: "text" },
            { k: "heading_line2", label: "Heading line 2 (gold)", type: "text" },
            { k: "intro", label: "Intro paragraph", type: "textarea" },
            { k: "governor_label", label: "Governor — small label", type: "text" },
            { k: "governor_title", label: "Governor — title", type: "text" },
            { k: "governor_subtitle", label: "Governor — subtitle", type: "text" },
            { k: "governor_paragraph", label: "Governor — paragraph", type: "textarea" },
            { k: "ambassador_label", label: "Ambassador — small label", type: "text" },
            { k: "ambassador_title", label: "Ambassador — title", type: "text" },
            { k: "ambassador_title_2", label: "Ambassador — title line 2 (gold)", type: "text" },
            { k: "ambassador_paragraph", label: "Ambassador — paragraph", type: "textarea" },
            { k: "riders_count", label: "Stat: riders count", type: "text" },
            { k: "riders_label", label: "Stat: riders label", type: "text" },
            { k: "open_positions", label: "Stat: open positions count", type: "text" },
            { k: "open_positions_label", label: "Stat: open positions label", type: "text" },
            { k: "open_positions_note", label: "Stat: button label (e.g. 'Apply Now →')", type: "text" },
            { k: "open_positions_url", label: "Stat: application form URL (makes the card a clickable link)", type: "text" },
            { k: "open_positions_enabled", label: "Show the 'Open Ambassador Roles' box", type: "toggle" },
        ],
    },
    {
        key: "leaders",
        title: "Leadership Gallery",
        description: "Section 03 — heading for the cofounder/ambassador grid.",
        fields: [
            { k: "eyebrow", label: "Eyebrow", type: "text" },
            { k: "heading_line1", label: "Heading line 1", type: "text" },
            { k: "heading_line2", label: "Heading line 2", type: "text" },
            { k: "cofounders_label", label: "Cofounders block label", type: "text" },
            { k: "ambassadors_label", label: "Ambassadors block label", type: "text" },
        ],
    },
    {
        key: "merch",
        title: "Merchandise (section header)",
        description: "Section 04 header text — articles themselves are managed in the Merchandise tab.",
        fields: [
            { k: "eyebrow", label: "Eyebrow", type: "text" },
            { k: "heading_line1", label: "Heading line 1", type: "text" },
            { k: "heading_line2", label: "Heading line 2 (gold)", type: "text" },
            { k: "paragraph", label: "Tagline / paragraph", type: "textarea" },
            { k: "empty_state", label: "Empty-state message (when no articles)", type: "text" },
        ],
    },
    {
        key: "gallery",
        title: "Meraki Riders Gallery (section header)",
        description: "Section 03 header text — ride folders & photos are managed in the Gallery tab.",
        fields: [
            { k: "eyebrow", label: "Eyebrow", type: "text" },
            { k: "heading_line1", label: "Heading line 1", type: "text" },
            { k: "heading_line2", label: "Heading line 2 (gold)", type: "text" },
            { k: "paragraph", label: "Tagline / paragraph", type: "textarea" },
            { k: "empty_state", label: "Empty-state message (when no galleries)", type: "text" },
        ],
    },
    {
        key: "dashboard",
        title: "Dashboard (Looker)",
        description: "Looker Studio embed + CTA.",
        fields: [
            { k: "eyebrow", label: "Eyebrow", type: "text" },
            { k: "heading_line1", label: "Heading line 1", type: "text" },
            { k: "heading_line2", label: "Heading line 2 (gold)", type: "text" },
            { k: "paragraph", label: "Paragraph", type: "textarea" },
            { k: "looker_url", label: "Looker public URL (opens in new tab)", type: "text" },
            { k: "looker_embed_url", label: "Looker embed URL (for iframe)", type: "text" },
            { k: "cta_label", label: "CTA button label", type: "text" },
        ],
    },
    {
        key: "join",
        title: "Join Modal",
        description: "The modal that opens when visitors click Join Us.",
        fields: [
            { k: "heading_line1", label: "Heading line 1", type: "text" },
            { k: "heading_line2", label: "Heading line 2 (gold)", type: "text" },
            { k: "paragraph", label: "Paragraph", type: "textarea" },
            { k: "option1_label", label: "Option 1 — label", type: "text" },
            { k: "option1_url", label: "Option 1 — URL", type: "text" },
            { k: "option1_desc", label: "Option 1 — description", type: "text" },
            { k: "option2_label", label: "Option 2 — label", type: "text" },
            { k: "option2_url", label: "Option 2 — URL", type: "text" },
            { k: "option2_desc", label: "Option 2 — description", type: "text" },
        ],
    },
    {
        key: "footer",
        title: "Footer",
        description: "Bottom of the page.",
        fields: [
            { k: "tagline", label: "Tagline", type: "textarea" },
            { k: "contact_email", label: "Contact email (mailto link)", type: "text" },
            { k: "contact_label", label: "Contact label (small text above the email)", type: "text" },
            { k: "whatsapp_number", label: "WhatsApp number (with country code, digits only — leave empty to hide)", type: "text" },
            { k: "whatsapp_message", label: "WhatsApp default message (pre-fills the chat)", type: "textarea" },
            { k: "whatsapp_label", label: "WhatsApp label (small text above the number)", type: "text" },
            { k: "copyright", label: "Copyright text", type: "text" },
            { k: "rightside", label: "Right-side line", type: "text" },
        ],
    },
];

function ImageField({ value, onChange, fieldKey }) {
    const inputRef = useRef(null);
    const onFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
            toast.error("Original file is too large. Pick something under 5MB.");
            return;
        }
        try {
            const b64 = await fileToBase64(file);
            onChange(b64);
        } catch {
            toast.error("Couldn't process this image. Try a different file.");
        }
    };
    const hasImage = value && value.length > 0;
    return (
        <div className="flex items-start gap-4">
            <div className="w-24 h-24 border border-meraki-gold/20 bg-black flex items-center justify-center overflow-hidden shrink-0">
                {hasImage ? <img src={value} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-6 h-6 text-meraki-muted" />}
            </div>
            <div className="flex-1 space-y-2">
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/*"
                    onChange={onFile}
                    data-testid={`img-${fieldKey}`}
                    className="text-xs text-meraki-muted file:mr-4 file:py-2 file:px-4 file:border file:border-meraki-gold/30 file:bg-transparent file:text-meraki-gold file:uppercase file:tracking-wider file:text-[10px] hover:file:bg-meraki-gold/10"
                />
                {hasImage && (
                    <button
                        type="button"
                        onClick={() => { onChange(""); if (inputRef.current) inputRef.current.value = ""; }}
                        className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-meraki-muted hover:text-red-400 transition-colors"
                    >
                        <X className="w-3 h-3" /> Clear (use default)
                    </button>
                )}
                <div className="text-[10px] text-meraki-muted/70">Max 2MB. Leave empty to use the default image.</div>
            </div>
        </div>
    );
}

export default function AdminContent() {
    const { content, reload } = useContent();
    const [draft, setDraft] = useState(content);
    const [saving, setSaving] = useState(false);

    useEffect(() => { setDraft(content); }, [content]);

    const updateField = (section, key, value) => {
        setDraft((d) => ({ ...d, [section]: { ...d[section], [key]: value } }));
    };

    const onSave = async () => {
        setSaving(true);
        try {
            // Send only string fields per section
            const payload = {};
            for (const section of Object.keys(CONTENT_DEFAULTS)) {
                payload[section] = {};
                for (const key of Object.keys(CONTENT_DEFAULTS[section])) {
                    payload[section][key] = String(draft?.[section]?.[key] ?? "");
                }
            }
            await api.put("/content", payload);
            await reload();
            toast.success("Content saved");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail, err));
        } finally {
            setSaving(false);
        }
    };

    const onResetAll = () => {
        if (!window.confirm("Reset ALL content to defaults? This will overwrite your edits.")) return;
        setDraft(CONTENT_DEFAULTS);
        toast.success("Reset locally — click Save to apply.");
    };

    return (
        <AdminLayout>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.4em] text-meraki-gold mb-3">Edit Landing Page</div>
                    <h1 className="font-display text-4xl md:text-5xl uppercase text-meraki-text leading-tight">Content & Images</h1>
                    <p className="text-meraki-muted text-sm mt-3 max-w-xl">Edit every text, button, and image on the public landing page. Changes go live after you save.</p>
                </div>
                <div className="flex gap-3">
                    <button
                        type="button"
                        onClick={onResetAll}
                        data-testid="reset-content-btn"
                        className="flex items-center gap-2 px-5 py-3 border border-meraki-gold/30 text-meraki-text text-xs uppercase tracking-[0.25em] hover:border-meraki-gold transition-colors"
                    >
                        <RotateCcw className="w-3 h-3" /> Reset all
                    </button>
                    <button
                        onClick={onSave}
                        disabled={saving}
                        data-testid="save-content-btn"
                        className="flex items-center gap-2 px-6 py-3 bg-meraki-gold text-black text-xs uppercase tracking-[0.25em] font-bold hover:bg-meraki-gold-hover transition-colors disabled:opacity-50"
                    >
                        <Save className="w-3 h-3" /> {saving ? "Saving..." : "Save changes"}
                    </button>
                </div>
            </div>

            <div className="space-y-12">
                {sections.map((s) => (
                    <div key={s.key} className="bg-meraki-surface border border-meraki-gold/20 p-6 md:p-8" data-testid={`section-${s.key}`}>
                        <div className="border-b border-meraki-gold/20 pb-4 mb-6">
                            <h2 className="font-heading uppercase tracking-[0.25em] text-meraki-gold text-sm">{s.title}</h2>
                            <p className="text-xs text-meraki-muted mt-2">{s.description}</p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            {s.fields.map((f) => (
                                <div key={f.k} className={f.type === "textarea" || f.type === "image" ? "md:col-span-2" : ""}>
                                    <label className="block text-[10px] uppercase tracking-[0.25em] text-meraki-muted mb-2">{f.label}</label>
                                    {f.type === "text" && (
                                        <input
                                            data-testid={`field-${s.key}-${f.k}`}
                                            value={draft?.[s.key]?.[f.k] ?? ""}
                                            onChange={(e) => updateField(s.key, f.k, e.target.value)}
                                            className="w-full bg-black border border-meraki-gold/20 px-4 py-3 text-meraki-text focus:outline-none focus:border-meraki-gold"
                                        />
                                    )}
                                    {f.type === "toggle" && (() => {
                                        const on = String(draft?.[s.key]?.[f.k] ?? "true") !== "false";
                                        return (
                                            <button
                                                type="button"
                                                data-testid={`field-${s.key}-${f.k}`}
                                                onClick={() => updateField(s.key, f.k, on ? "false" : "true")}
                                                className={`inline-flex items-center gap-3 px-4 py-3 border text-xs uppercase tracking-[0.25em] transition-colors ${on ? "border-meraki-gold text-meraki-gold" : "border-meraki-muted/40 text-meraki-muted"}`}
                                            >
                                                <span className={`relative w-9 h-5 rounded-full transition-colors ${on ? "bg-meraki-gold" : "bg-meraki-muted/40"}`}>
                                                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-black transition-all ${on ? "left-[18px]" : "left-0.5"}`} />
                                                </span>
                                                {on ? "Enabled" : "Disabled"}
                                            </button>
                                        );
                                    })()}
                                    {f.type === "textarea" && (
                                        <textarea
                                            data-testid={`field-${s.key}-${f.k}`}
                                            rows={3}
                                            value={draft?.[s.key]?.[f.k] ?? ""}
                                            onChange={(e) => updateField(s.key, f.k, e.target.value)}
                                            className="w-full bg-black border border-meraki-gold/20 px-4 py-3 text-meraki-text focus:outline-none focus:border-meraki-gold"
                                        />
                                    )}
                                    {f.type === "image" && (
                                        <ImageField
                                            value={draft?.[s.key]?.[f.k] ?? ""}
                                            onChange={(v) => updateField(s.key, f.k, v)}
                                            fieldKey={`${s.key}-${f.k}`}
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            <div className="mt-12 pt-6 border-t border-meraki-gold/15 flex justify-end gap-3">
                <button
                    onClick={onSave}
                    disabled={saving}
                    data-testid="save-content-btn-bottom"
                    className="flex items-center gap-2 px-6 py-3 bg-meraki-gold text-black text-xs uppercase tracking-[0.25em] font-bold hover:bg-meraki-gold-hover transition-colors disabled:opacity-50"
                >
                    <Save className="w-3 h-3" /> {saving ? "Saving..." : "Save changes"}
                </button>
            </div>
        </AdminLayout>
    );
}
